import { RootState } from 'modules/store';
import { ErrorObject } from 'types/global';
import Errors from 'modules/shared/error/errors';
import { createAsyncThunk } from '@reduxjs/toolkit';
import QueryString from 'query-string';
import { downloadCSVFile } from 'utils/dowloadCSVFile';
import {
  PaginatedResponse,
  AvsError,
  InternationalCountry,
  InternationalVerificationRecord,
  InternationalVerificationDetails,
  InternationalVerificationBulkDetails,
} from '../avs.types';
import AvsService from '../avs.service';

function getPage(): number {
  const queryParams = QueryString.parse(window.location.search);
  return Number(queryParams.page || 1);
}

export const uploadInternationalBulkVerification = createAsyncThunk<
  InternationalVerificationBulkDetails,
  {
    appToken: string;
    data: any;
  },
  {
    state: RootState;
    rejectValue: { message: string } | AvsError | ErrorObject;
  }
>(
  'avs/international-bulk-verification',
  async ({ appToken, data }, { getState, rejectWithValue, dispatch }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      return await AvsService.uploadInternationalBulkVerification({
        appToken,
        appEnvironment,
        data,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        status: Errors.errorStatusOrStatusCode(error),
        errorCode: Errors.errorCode(error),
      });
    } finally {
      const page = getPage();
      dispatch(getInternationalVerificationRecords({ appToken, page }));
    }
  },
);

export const getInternationalVerificationRecords = createAsyncThunk<
  PaginatedResponse<InternationalVerificationRecord>,
  {
    appToken: string;
    page: number;
  },
  {
    state: RootState;
    rejectValue: { message: string } | AvsError | ErrorObject;
  }
>(
  'avs/get-international-verification-records',
  async ({ appToken, page }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      return await AvsService.getInternationalVerificationRecords({
        appToken,
        appEnvironment,
        page,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const getInternationalBulkVerificationResult = createAsyncThunk<
  InternationalVerificationBulkDetails,
  {
    appToken: string;
    requestId: string;
  },
  {
    state: RootState;
    rejectValue: { message: string } | AvsError | ErrorObject;
  }
>(
  'avs/get-international-bulk-verification-result',
  async ({ appToken, requestId }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      return await AvsService.getInternationalBulkVerificationResult({
        appToken,
        requestId,
        appEnvironment,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const getInternationalBulkVerificationResultData = createAsyncThunk<
  PaginatedResponse<InternationalVerificationDetails>,
  {
    appToken: string;
    requestId: string;
    isCsvFile?: boolean;
    page?: number;
  },
  {
    state: RootState;
    rejectValue: { message: string } | AvsError | ErrorObject;
  }
>(
  'avs/get-international-bulk-verification-result-data',
  async (
    { appToken, requestId, isCsvFile, page },
    { getState, rejectWithValue },
  ) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      const response = await AvsService.getInternationalBulkVerificationResultData({
        appToken,
        requestId,
        isCsvFile,
        appEnvironment,
        page,
      });

      if (isCsvFile) {
        downloadCSVFile(response, `international_verification_${requestId}.csv`);
      }
      return response;
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const getInternationalVerificationResult = createAsyncThunk<
  InternationalVerificationDetails,
  {
    appToken: string;
    requestId: string;
    isCsvFile?: boolean;
  },
  {
    state: RootState;
    rejectValue: { message: string } | AvsError | ErrorObject;
  }
>(
  'avs/get-international-verification-result',
  async ({ appToken, requestId, isCsvFile }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      const response = await AvsService.getInternationalVerificationResult({
        appToken,
        requestId,
        isCsvFile,
        appEnvironment,
      });

      if (isCsvFile) {
        downloadCSVFile(response, `international_verification_${requestId}.csv`);
      }
      return response;
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);

export const getInternationalCountries = createAsyncThunk<
  InternationalCountry[],
  {
    appToken: string;
  },
  {
    state: RootState;
    rejectValue: { message: string } | AvsError | ErrorObject;
  }
>(
  'avs/get-international-countries',
  async ({ appToken }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment } = getState().root.global;
    try {
      return await AvsService.getInternationalCountries({
        appToken,
        appEnvironment,
      });
    } catch (error: unknown) {
      return rejectWithValue({
        message: Errors.selectMessage(error),
        errorCode: Errors.errorCode(error),
      });
    }
  },
);
